/**
 * Guardrails Settings Page
 *
 * Two-section page for managing guardrails:
 * 1. Platform guardrails (system admin only) — applies to ALL tenants
 * 2. Tenant guardrails (admin) — scoped to current tenant
 *
 * Sprint 50, Task C2
 */

import { useState, useEffect } from 'react';
import { Loader2, Save, ShieldAlert, Plus, X, Building2, Globe } from 'lucide-react';
import { Card } from '@/components/ui';
import { toast } from 'sonner';
import { useIsSystemAdmin } from '@/components/SystemAdminGuard';
import {
  usePlatformGuardrails,
  useUpdatePlatformGuardrails,
  useTenantGuardrails,
  useUpdateTenantGuardrails,
} from '@/hooks/use-guardrails';

// =============================================================================
// Editable Guardrails List Component
// =============================================================================

interface GuardrailsListEditorProps {
  guardrails: string[];
  onChange: (guardrails: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

function GuardrailsListEditor({
  guardrails,
  onChange,
  placeholder = 'Enter a guardrail rule…',
  disabled = false,
}: GuardrailsListEditorProps) {
  const [newItem, setNewItem] = useState('');

  const addItem = () => {
    const trimmed = newItem.trim();
    if (trimmed && !guardrails.includes(trimmed)) {
      onChange([...guardrails, trimmed]);
      setNewItem('');
    }
  };

  const removeItem = (index: number) => {
    onChange(guardrails.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, value: string) => {
    const updated = [...guardrails];
    updated[index] = value;
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      {guardrails.map((item, index) => (
        <div key={index} className="flex items-start gap-2 group">
          <span className="text-xs text-muted-foreground mt-2.5 w-5 text-right shrink-0">
            {index + 1}.
          </span>
          <textarea
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
            disabled={disabled}
            rows={1}
            className="flex-1 rounded-md border bg-background px-3 py-2 text-sm resize-y min-h-[36px] focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
          {!disabled && (
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="mt-2 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}

      {!disabled && (
        <div className="flex items-center gap-2 mt-3">
          <span className="w-5" />
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addItem();
              }
            }}
            placeholder={placeholder}
            className="flex-1 rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            type="button"
            onClick={addItem}
            disabled={!newItem.trim()}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      )}

      {guardrails.length === 0 && (
        <p className="text-sm text-muted-foreground italic pl-7">
          No guardrails configured. Add rules to ensure safe and compliant agent behaviour.
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Page Component
// =============================================================================

export function GuardrailsSettingsPage() {
  const isSystemAdmin = useIsSystemAdmin();

  // Platform guardrails (system admin only)
  const {
    data: platformData,
    isLoading: isLoadingPlatform,
  } = usePlatformGuardrails();
  const updatePlatformMutation = useUpdatePlatformGuardrails();

  // Tenant guardrails
  const {
    data: tenantData,
    isLoading: isLoadingTenant,
  } = useTenantGuardrails();
  const updateTenantMutation = useUpdateTenantGuardrails();

  // Local state — guardrails
  const [platformGuardrails, setPlatformGuardrails] = useState<string[]>([]);
  const [tenantGuardrails, setTenantGuardrails] = useState<string[]>([]);
  const [platformDirty, setPlatformDirty] = useState(false);
  const [tenantDirty, setTenantDirty] = useState(false);

  // Initialize — guardrails
  useEffect(() => {
    if (platformData) {
      setPlatformGuardrails(platformData.guardrails);
      setPlatformDirty(false);
    }
  }, [platformData]);

  useEffect(() => {
    if (tenantData) {
      setTenantGuardrails(tenantData);
      setTenantDirty(false);
    }
  }, [tenantData]);

  // Track dirty — guardrails
  useEffect(() => {
    if (platformData) {
      setPlatformDirty(
        JSON.stringify(platformData.guardrails) !== JSON.stringify(platformGuardrails)
      );
    }
  }, [platformData, platformGuardrails]);

  useEffect(() => {
    if (tenantData) {
      setTenantDirty(
        JSON.stringify(tenantData) !== JSON.stringify(tenantGuardrails)
      );
    }
  }, [tenantData, tenantGuardrails]);

  const handleSavePlatform = async () => {
    try {
      await updatePlatformMutation.mutateAsync({ guardrails: platformGuardrails });
      setPlatformDirty(false);
      toast.success('Platform guardrails saved');
    } catch {
      toast.error('Failed to save platform guardrails');
    }
  };

  const handleSaveTenant = async () => {
    try {
      await updateTenantMutation.mutateAsync(tenantGuardrails);
      setTenantDirty(false);
      toast.success('Tenant guardrails saved');
    } catch {
      toast.error('Failed to save tenant guardrails');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-orange-500" />
          Guardrails
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Define rules and constraints that all agents must follow. Guardrails are
          additive — platform, tenant, and agent-level rules are merged at runtime.
        </p>
      </div>

      <div className="max-w-3xl space-y-6">
        {/* Platform Guardrails (System Admin Only) */}
        {isSystemAdmin && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-500" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    Platform Guardrails
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Applied to ALL tenants and agents across the platform
                  </p>
                </div>
              </div>
              <button
                onClick={handleSavePlatform}
                disabled={!platformDirty || updatePlatformMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updatePlatformMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                Save
              </button>
            </div>

            {isLoadingPlatform ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <GuardrailsListEditor
                guardrails={platformGuardrails}
                onChange={setPlatformGuardrails}
                placeholder="e.g. Never reveal internal system architecture details"
              />
            )}
          </Card>
        )}

        {/* Tenant Guardrails */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  Tenant Guardrails
                </h3>
                <p className="text-xs text-muted-foreground">
                  Applied to all agents in this workspace
                </p>
              </div>
            </div>
            <button
              onClick={handleSaveTenant}
              disabled={!tenantDirty || updateTenantMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateTenantMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              Save
            </button>
          </div>

          {isLoadingTenant ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <GuardrailsListEditor
              guardrails={tenantGuardrails}
              onChange={setTenantGuardrails}
              placeholder="e.g. Always verify user identity before sharing sensitive data"
            />
          )}
        </Card>

        {/* Info Note */}
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4">
          <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
            How guardrails work
          </h4>
          <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
            <li>
              <strong>Tenant</strong> guardrails are set by workspace admins and apply to all agents in this workspace.
            </li>
            <li>
              <strong>Agent</strong> guardrails are configured per-agent in the Agent Editor → Prompts tab.
            </li>
            <li>
              All levels are <strong>merged additively</strong> at runtime — no level can override another.
            </li>
            <li>
              <strong>Data Governance</strong> policies are configured in the Governance section and apply independently of guardrails.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
